import type { ReactNode } from 'react';

import type { ApiProblemKind } from '../api/problem';

/**
 * One presentation for every classified failure, so the copy is written once.
 *
 * The important case is `conflict`. A `409` from verify-field means another
 * reviewer settled the field between the queue being rendered and this
 * verification being submitted — the reviewer's work did not fail, and the field
 * is not broken. `apps/api/src/rejections.ts` is explicit that the conflict
 * message is deliberately vague ("a support answer rather than an oracle over
 * another reviewer's activity"), so the copy here explains the situation without
 * naming anyone. It renders in the *neutral* tone with a Reload action, never as
 * an error: styling it red teaches reviewers that a normal outcome is a fault.
 *
 * `not_found` is the other case with a rule attached. An out-of-scope contract
 * key and a nonexistent one are indistinguishable **by design** — a `403` there
 * would confirm a contract's existence to someone not entitled to know it. So
 * this renders exactly one sentence covering both, and the surface must not add
 * a second one that narrows it.
 *
 * CLM-65 splits the 401 in two. `unauthenticated` keeps "Session expired" — no
 * token, or one past its `exp`. `rejected` is a 401 the gateway returned for a
 * token that was still valid: the first production sign-in, where the app
 * registration issued v1 tokens and the API expected the v2.0 issuer. Telling
 * that user to sign in again sends them round a loop that cannot end, so the
 * copy says plainly that it is a configuration mismatch and asks for the
 * correlation id instead. The classifier is `unauthenticatedKind` in problem.ts.
 *
 * The 403 is split the same way, from the body's `reason` (30 Sep 2026). A
 * CEQ-CLM-Admin member with no CEQ-CLM-Scope-* group was told *"Your role may
 * not take this action"* on every call, with *"The gateway said: Not
 * authorised"* underneath; her role was fine, and nothing on the page pointed
 * at the scope group that was actually missing. `forbidden` keeps its copy,
 * which is true for the role gate and the best available for a gateway that
 * sends no reason. The three sign-in causes get their own (`forbiddenKind` in
 * problem.ts says why they are kinds, not a detail on `forbidden`).
 */

const COPY: Readonly<
  Record<
    ApiProblemKind,
    { readonly heading: string; readonly body: string; readonly tone: 'bad' | 'up' | 'out' }
  >
> = {
  conflict: {
    heading: 'Another reviewer settled this field',
    body:
      'Someone else verified or corrected this field, or the value was re-extracted, after this queue was loaded. Nothing you did failed and nothing was recorded against the old value — the row simply moved on. Reload it to see where it stands now.',
    tone: 'up',
  },
  not_found: {
    heading: 'Not found, or outside your scope',
    body:
      'This key does not resolve for you. The API cannot tell you which of the two it is, and deliberately does not: answering differently for a contract that exists but is outside your scope would confirm its existence to someone not entitled to know it.',
    tone: 'out',
  },
  forbidden: {
    heading: 'Your role may not take this action',
    body:
      'The gateway re-derives your role from your token on every call and refused this one. That is the boundary working, not a fault in this page. If you believe you should hold this permission, ask whoever manages CLM access; if it was granted recently, sign out and back in so your token picks it up.',
    tone: 'out',
  },
  /* The three below are the sign-in itself, not this action: every call fails
     the same way until the groups change, so each says what to ask for and
     that a new sign-in is what picks it up. Group *names* only, which the
     SPA already carries (permissions.ts) — never an id, and nothing from the
     gateway beyond the code. Neutral tone: these are the boundary working,
     as `forbidden` is, not a defect in this page.

     Worded around what the gateway knows, never as a fact about the caller's
     membership. The gateway knows only which of the token's groups are
     *mapped in this deployment's AuthConfig*; an unmapped id is skipped
     without a word (packages/auth authorization.ts). CEQ-Legal may be
     legitimately unmapped (it warns rather than fails at start-up, B-07) and
     practice scope groups may be only partly mapped, so "you are not in any
     CLM scope group" can be false for someone who is — and would send her to
     ask for a group she already holds, the wrong fix this copy exists to
     remove. Hence the correlation-id line for "I believe I already am". */
  no_clm_scope_group: {
    heading: 'No CLM scope group recognised in your sign-in',
    body:
      'Your CLM role is recognised, but none of the groups in your sign-in is one this CLM deployment recognises as a scope group (CEQ-CLM-Scope-*), so you have no contracts in scope and the gateway refuses every call. Ask whoever manages CLM access to add you to a scope group, then sign out and back in. If you believe you are already in one, quote the correlation id to them: the group may not be mapped in this deployment.',
    tone: 'out',
  },
  no_clm_role_group: {
    heading: 'No CLM role group recognised in your sign-in',
    body:
      'Your sign-in was accepted, but none of the groups in it is one this CLM deployment recognises as a role group (CEQ-CLM-Reader, CEQ-CLM-Contributor, CEQ-CLM-Admin or CEQ-Legal), so the gateway refuses every call. Ask whoever manages CLM access for the role your work needs, and for a CLM scope group (CEQ-CLM-Scope-*) if you have none, then sign out and back in. If you believe you are already in one, quote the correlation id to them: the group may not be mapped in this deployment.',
    tone: 'out',
  },
  no_groups_claim: {
    heading: 'Your sign-in carries no group memberships',
    body:
      'The token the API received has no groups claim, so the gateway cannot find a CLM role or scope for you and refuses every call. Either you are in no security groups, or the app registration is not configured to send them; signing in again will not change that. Quote the correlation id to whoever manages CLM access.',
    tone: 'out',
  },
  unauthenticated: {
    heading: 'Session expired',
    body: 'Your sign-in is no longer valid. Sign in again to continue.',
    tone: 'up',
  },
  rejected: {
    heading: 'Sign-in not accepted by the API',
    body:
      'The API rejected your sign-in token. Signing in again will not help; this is a configuration mismatch between the web app and the API. Quote the correlation id to the CLM team.',
    tone: 'bad',
  },
  validation: {
    heading: 'The request was rejected',
    body:
      'The gateway validated this request against the API contract and refused it. That is a defect in this page rather than something you can fix by retrying — report it with the correlation id below.',
    tone: 'bad',
  },
  not_implemented: {
    heading: 'Not built yet',
    body:
      'Your sign-in and permissions were accepted; the behaviour behind this endpoint does not exist yet. Nothing is wrong with your request, and retrying will not change the answer.',
    tone: 'out',
  },
  off_contract: {
    heading: 'The API answered off-contract',
    body:
      'The gateway responded, but not with the shape this build was compiled against. Nothing from that payload is shown here, because a response that does not match the contract is a defect to report rather than data to display.',
    tone: 'bad',
  },
  client_scope_refusal: {
    heading: 'This page tried to send a scope parameter',
    body:
      'The API client refused to construct the request. CLM scope is resolved server-side from Entra group claims and is never a client-supplied parameter (NFR-06), so this is a bug in this page — report it.',
    tone: 'bad',
  },
  unknown: {
    heading: 'The request did not complete',
    body: 'Something went wrong that this page cannot classify. Retrying is reasonable once.',
    tone: 'bad',
  },
};

export function ErrorNotice({
  kind,
  detail,
  correlation_id,
  action,
}: {
  readonly kind: ApiProblemKind;
  /** The server's own message, when it is safe and useful to show. */
  readonly detail?: string | null;
  readonly correlation_id?: string | null;
  /** e.g. the Reload-this-row control for a conflict. */
  readonly action?: ReactNode;
}) {
  const copy = COPY[kind];
  // `alert` interrupts; `status` is announced politely. A conflict and an
  // unbuilt endpoint are not emergencies, so they do not interrupt.
  const isAlert = copy.tone === 'bad';

  return (
    <div className={`banner ${copy.tone}`} role={isAlert ? 'alert' : 'status'}>
      <p className="notice-head">
        <strong>{copy.heading}</strong>
      </p>
      <p>{copy.body}</p>
      {detail !== undefined && detail !== null && detail !== '' ? (
        <p className="mut">The gateway said: {detail}</p>
      ) : null}
      {correlation_id !== undefined && correlation_id !== null && correlation_id !== '' ? (
        <p className="mut">
          Correlation id <code>{correlation_id}</code>
        </p>
      ) : null}
      {action}
    </div>
  );
}
